import React, { FC, RefObject, useContext, useMemo } from 'react'

import SvgZoomIn from '@Assets/SVG/ZoomIn'
import { ProductContext } from '@Components/Product/Context'
import { getChipAssets } from '@Components/Product/Display/Helpers/ChipAssets'
import {
  Anchor,
  BrandSection,
  FullscreenSliderDotWrapper,
  Image,
  StarRatingWrapper,
  WishlistWrapper,
  ZoominButton,
  ZoomWrapper,
} from '@Components/Product/Display/styledElements'
import CircleDots from '@Components/Product/Slider/CircleDots'
import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { StarRating } from '@Components/StarRating'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { convertToGTM, convertToMParticle } from '@Globals/CMS/ProductHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { useScrollTo } from '@Globals/useScrollTo'

import ProductChipsLayout from './ProductChips'

interface Props {
  scrollToRef: RefObject<HTMLDivElement>
}

const SliderOverlay: FC<Props> = ({ scrollToRef }) => {
  const { t } = useTranslation('cms')
  const product = useContext(ProductContext)
  const {
    isFullscreen,
    isSwipedUp,
    isVideoSlide,
    toggleFullscreen,
  } = useContext(SliderContext)
  const {
    isZoomedIn,
  } = useContext(ControllerContext)
  const { store } = useContext(MetadataContext)
  const scrollToReviewBlock = useScrollTo(scrollToRef)
  const chipOptions = useMemo(
    () => getChipAssets({ product, store }),
    [product, store],
  )
  if (!product) {
    return null
  }

  return (
    <>
      <ProductChipsLayout options={chipOptions} />
      <WishlistWrapper>
        <AddToWishList
          gtmProduct={convertToGTM(product)}
          id={product.id}
          mParticleProduct={convertToMParticle(product)}
        />
      </WishlistWrapper>
      {(isSwipedUp || !isFullscreen) && !isVideoSlide && (
        <ZoomWrapper>
          <ZoominButton
            aria-label={t('GALLERY_ZOOM_LABEL')}
            onClick={toggleFullscreen}
          >
            <SvgZoomIn />
          </ZoominButton>
        </ZoomWrapper>
      )}
      {!isSwipedUp && isFullscreen && !isZoomedIn
        && <FullscreenSliderDotWrapper><CircleDots /></FullscreenSliderDotWrapper>}
      <BrandSection>
        {product.brand && (
          <Anchor href={product.brand.url?.toString()}>
            <Image
              alt={product.brand.id || ''}
              src={product.brand.logo || ''}
              title={product.brand.title || ''}
            />
          </Anchor>
        )}
        <CircleDots />
        <StarRatingWrapper onClick={() => scrollToReviewBlock()}>
          <StarRating rating={product.productReviews?.averageRating || 0} />
        </StarRatingWrapper>
      </BrandSection>
    </>
  )
}

export default SliderOverlay
